"use client"

import Image from "next/image"
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from "@headlessui/react"
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline"
import { clsx } from "clsx"
import { useState } from "react"
import { navigation as fullNavigation } from "./DesktopFrame"
import wideLogoImage from "../images/wide-logo.svg"

interface Props {
    items: {
        name: string
        current: boolean
    }[]
}

export function MobileFrameClientSide({ items }: Props) {
    const [sidebarOpen, setSidebarOpen] = useState(false)

    const selectedNavigation = items.map((n) => n.name)
    const filteredNavigation = fullNavigation
        .filter((n) => selectedNavigation.includes(n.name))
        .map((n) => ({
            ...n,
            current: items.find((nav) => nav.name === n.name)?.current ?? false,
        }))

    const label = filteredNavigation.find((item) => item.current)?.name ?? ""

    return (
        <>
            <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-10 lg:hidden">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
                />

                <div className="fixed inset-0 flex">
                    <DialogPanel
                        transition
                        className="relative mr-16 flex w-full max-w-xs flex-1 transition duration-300 ease-in-out data-[closed]:-translate-x-full"
                    >
                        <TransitionChild>
                            <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                                <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                                    <span className="sr-only">Close sidebar</span>
                                    <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                                </button>
                            </div>
                        </TransitionChild>
                        <div className="bg-primary-600 flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-2">
                            <div className="flex h-16 shrink-0 items-center">
                                <Image className="h-10 w-auto" src={wideLogoImage} priority alt="ResearchWiseAI Logo" />
                            </div>
                            <nav className="flex flex-1 flex-col">
                                <ul className="-mx-2 space-y-1">
                                    {filteredNavigation.map((item) => (
                                        <li key={item.name}>
                                            <a
                                                href={item.href}
                                                className={clsx(
                                                    item.current
                                                        ? "bg-primary-700 text-white"
                                                        : "text-primary-200 hover:bg-primary-700 hover:text-white",
                                                    "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                                                )}
                                            >
                                                <item.icon
                                                    aria-hidden="true"
                                                    className={clsx(
                                                        item.current
                                                            ? "text-white"
                                                            : "text-primary-200 group-hover:text-white",
                                                        "size-6 shrink-0"
                                                    )}
                                                />
                                                {item.name}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>
                    </DialogPanel>
                </div>
            </Dialog>
            <div className="bg-primary-600 sticky top-0 flex items-center gap-x-6 px-4 py-4 shadow-sm sm:px-6 lg:hidden">
                <button
                    type="button"
                    onClick={() => setSidebarOpen(true)}
                    className="text-primary-200 -m-2.5 p-2.5 lg:hidden"
                >
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon aria-hidden="true" className="size-6" />
                </button>
                <div className="flex-1 text-sm/6 font-semibold text-white">{label}</div>
            </div>
        </>
    )
}
